import type { Reading } from "@spf/contracts";

/**
 * The power-flow diagram's model, derived from a reading.
 *
 * This is presentation, not business logic, and it deliberately lives here
 * rather than in a component or in the API. The sign convention it reads is a
 * property of the data and is documented on the contract; turning a signed
 * number into an arrow and a word is a decision about how to draw it, and
 * docs/REQUIREMENTS.md section 3 forbids the API returning view-shaped data.
 * Keeping it out of the component is what section 3 asks for on the other
 * side, and is what lets it be tested without rendering anything.
 *
 * Solar joined in slice 5c, once its fields were verified against Growatt's
 * own decode and the inverter's counters (docs/DECISIONS/0028).
 */

export type NodeId = "solar" | "grid" | "inverter" | "battery" | "home";

export type FlowId = "solar" | "grid" | "battery" | "load";

export interface Flow {
  id: FlowId;
  /** Null when nothing is moving, so the diagram draws no arrowhead. */
  from: NodeId | null;
  to: NodeId | null;
  /** Always positive. The direction is carried by `from` and `to`. */
  watts: number;
  /**
   * What is happening, in words. This is the diagram's text equivalent, which
   * docs/REQUIREMENTS.md section 4 requires alongside colour and animation --
   * so it is rendered, not only announced.
   */
  description: string;
}

export interface FlowModel {
  flows: Flow[];
  socPct: number;
  batteryVoltageV: number;
  gridVoltageV: number;
  inverterTempC: number;
}

/**
 * Exactly zero, not a threshold. `batteryPowerW` is the inverter's own signed
 * integer of watts, so zero means the battery is neither charging nor
 * discharging -- and a battery sitting at rest used to render as
 * "0 W discharging", which is a statement about direction that is not true.
 */
function batteryFlow(watts: number): Flow {
  if (watts === 0) {
    return { id: "battery", from: null, to: null, watts: 0, description: "Battery idle" };
  }
  const charging = watts < 0;
  return {
    id: "battery",
    from: charging ? "inverter" : "battery",
    to: charging ? "battery" : "inverter",
    watts: Math.abs(watts),
    description: charging ? "Battery charging" : "Battery discharging",
  };
}

function solarFlow(watts: number): Flow {
  // Zero at night is the panels reporting nothing, which is a measurement.
  if (watts <= 0) {
    return { id: "solar", from: null, to: null, watts: 0, description: "No solar generation" };
  }
  return {
    id: "solar",
    from: "solar",
    to: "inverter",
    watts,
    description: "Generating from the panels",
  };
}

function gridFlow(watts: number): Flow {
  // This inverter is off-grid and does not export, so anything at or below
  // zero is "the grid is not carrying anything", not a reverse flow.
  if (watts <= 0) {
    return { id: "grid", from: null, to: null, watts: 0, description: "No grid import" };
  }
  return {
    id: "grid",
    from: "grid",
    to: "inverter",
    watts,
    description: "Importing from the grid",
  };
}

function loadFlow(watts: number): Flow {
  if (watts <= 0) {
    return { id: "load", from: null, to: null, watts: 0, description: "No load" };
  }
  return { id: "load", from: "inverter", to: "home", watts, description: "Supplying the home" };
}

export function toFlowModel(reading: Reading): FlowModel {
  return {
    flows: [
      solarFlow(reading.pvPowerW),
      gridFlow(reading.gridPowerW),
      batteryFlow(reading.batteryPowerW),
      loadFlow(reading.loadPowerW),
    ],
    socPct: reading.socPct,
    batteryVoltageV: reading.batteryVoltageV,
    gridVoltageV: reading.gridVoltageV,
    inverterTempC: reading.inverterTempC,
  };
}

/**
 * How thick to draw a flow, as a fraction from 0 to 1.
 *
 * Scaled against the largest flow on screen rather than against the inverter's
 * nameplate, because the question the diagram answers is "where is the power
 * going right now", and against a 5 kW rating a 130 W household load and a
 * 30 W one are the same hairline.
 */
export function weightOf(flow: Flow, flows: Flow[]): number {
  const largest = Math.max(...flows.map((f) => f.watts));
  if (largest <= 0 || flow.watts <= 0) return 0;
  return flow.watts / largest;
}
